/**
 * Copyright (c) [2024] Jangouts
 *
 * This software may be modified and distributed under the terms
 * of the MIT license.  See the LICENSE.txt file for details.
 */

import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  PhoneOff,
  MessageCircle,
} from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { actionCreators as callActions } from "../../state/ducks/call";
import {
  selectors as participantSelectors,
  actionCreators as participantsActions,
} from "../../state/ducks/participants";
import Participants from "../Participants";
import MessagesList from "../Chat/MessagesList";
import MessageForm from "../Chat/MessageForm";
import { mixer } from "../../utils/common";
import StreamsService from "../../utils/streams-service";
import janusApi from "../../janus-api";

/**
 * 通话中界面组件 - 手机端简化版
 * 全屏显示，仅包含两个用户头像、名字、通话时长和基本控制按钮
 */
const ActiveCall = ({
  callId,
  remoteUserId,
  remoteUsername,
  localAudioStream = null,
  remoteAudioStream = null,
  isMuted = false,
  onToggleMute,
  onEndCall,
  onHold,
  className = "",
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userInfo);
  const [callDuration, setCallDuration] = useState(0);
  //recording
  const [isSpeakerEnabled, setIsSpeakerEnabled] = useState(true);
  const [recording, setRecording] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const lastReadCountRef = useRef(0);

  const participant = useSelector((state) =>
    participantSelectors.localParticipant(state.participants),
  );
  const participants = useSelector((state) => state.participants);
  const messages = useSelector((state) => state.messages.list);

  let streams = [];
  let mixStreams;
  let isMutedButton = false;
  if (participant !== undefined) {
    const { display: username, audio, isPublisher } = participant;
    isMutedButton = audio ? false : true;

    if (participants.length > 0) {
      streams = participants.map((p) => StreamsService.get(p.id));
    }
    mixStreams = mixer(streams);

    //console.log('participants========1========', mixStreams);
  }

  const initialLoadRef = useRef(false);

  // useEffect(() => {
  //   if (recording) {
  //     let stream;
  //     let tracks = [];
  //     if (participant !== undefined) {
  //       stream = StreamsService.get(participant.id);
  //     }
  //     if (mixStreams) {
  //       //console.log('mixStreams====start', mixStreams)
  //       let captureAudio = mixStreams.getAudioTracks()[0];
  //       tracks.push({ type: "audio", capture: captureAudio, recv: true });
  //     }
  //     if (stream) {
  //       let captureVideo = stream.getVideoTracks()[0];
  //       tracks.push({
  //         type: "video",
  //         capture: captureVideo,
  //         recv: true,
  //         simulcast: false,
  //       });
  //     }
  //     //console.log('participants========1========', tracks);
  //     if (tracks.length > 0) {
  //       janusApi.changeTracks(tracks);
  //     }
  //   }
  // }, [streams]);

  // 初始标记已有消息为已读
  useEffect(() => {
    if (!initialLoadRef.current && messages.length > 0) {
      // 首次加载消息时，标记所有现有消息为已读
      lastReadCountRef.current = messages.length;
      initialLoadRef.current = true;
    }
  }, [messages.length]);

  // 当聊天窗口打开时，标记所有消息为已读
  useEffect(() => {
    if (chatOpen) {
      lastReadCountRef.current = messages.length;
    }
  }, [chatOpen, messages.length]);

  // 计算未读消息数量
  const unreadCount = (() => {
    // 如果没有消息，未读数为0
    if (messages.length === 0) return 0;
    // 聊天窗口打开时未读数为0
    if (chatOpen) return 0;
    // 计算未读数量
    return Math.max(0, messages.length - lastReadCountRef.current);
  })();

  // 更新通话时长
  useEffect(() => {
    const timer = setInterval(() => {
      setCallDuration((prev) => prev + 1);
    }, 1000);

    return () => {
      console.log("Active Call 清理函数被调用1");
      handleEndCall();
      clearInterval(timer);
    };
  }, []);

  const handleToggleMute = () => {
    dispatch(participantsActions.toggleAudio(participant.id));
  };

  // 切换扬声器
  const handleToggleSpeaker = () => {};

  const toggleChat = () => {
    setChatOpen(!chatOpen);
  };

  const handleEndCall = () => {
    console.log(`Ending call ${callId}`);
    stopRecord();
    setRecording(false);
    // 更新Redux状态
    dispatch(callActions.endCall(callId));

    if (onEndCall) {
      onEndCall();
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // 获取名字的第一个字符（支持中文）
  // const getFirstChar = (name) => {
  //   if (!name || name.trim().length === 0) return "?";
  //   return name.trim()
  //   .charAt(0)
  //   .toUpperCase();
  // };

  const localName = currentUser?.nickname || currentUser?.username || "我";
  const remoteName = remoteUsername || "对方";

  const record = async (mixStreams) => {
    if (recording) {
      return;
    }
    setRecording(true);
    await janusApi.record(mixStreams);
  };
  const stopRecord = async () => {
    console.log("stop record...");
    const res = await janusApi.recordStop();
    console.log("stop result...", res);
  };

  return (
    <div
      className={`active-call fixed inset-0 bg-gradient-to-b z-50 from-blue-900 to-gray-900 ${className}`}
    >
      {/* 全屏内容 */}
      <div className="h-full flex flex-col items-center justify-between p-6">
        {/* 顶部：两个用户头像和名字 */}
        <div className="w-full mt-12">
          <div className="flex flex-col items-center space-y-8">
            <Participants />
          </div>
        </div>

        {/* 中间：通话状态 */}
        <div className="text-center">
          <div className="text-3xl font-bold text-white mb-2">
            {formatDuration(callDuration)}
          </div>
          <div className="text-blue-300">通话进行中</div>
        </div>

        {/* 聊天弹窗 */}
        {chatOpen && (
          <div className="absolute inset-0 bg-black bg-opacity-80 z-50 flex flex-col p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-xl font-bold">消息</h3>
              <button onClick={toggleChat} className="text-white text-2xl">
                ×
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-gray-900 rounded-lg p-4">
              <MessagesList />
            </div>
            <div className="mt-4">
              <MessageForm />
            </div>
          </div>
        )}

        {/* 右上角消息提醒点 */}
        <div className="absolute top-4 right-4 z-40">
          <button
            onClick={toggleChat}
            className="relative flex items-center justify-center w-12 h-12 rounded-full bg-gray-800 hover:bg-gray-700 transition-all"
            title="消息"
          >
            <MessageCircle size={24} className="text-white" />
            {unreadCount > 0 && (
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
                <span className="text-xs text-white font-bold">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              </div>
            )}
          </button>
        </div>

        {/* 底部：控制按钮 */}
        <div className="w-full max-w-md mb-10">
          <div className="flex items-center justify-around">
            {/* <div className=" text-white" onClick={() => record(mixStreams)}>
              录制
            </div> */}
            {/* 消息按钮 - 放在静音按钮上面 */}
            <button
              onClick={toggleChat}
              className="flex flex-col items-center justify-center w-20 h-20 rounded-full bg-blue-600 hover:bg-blue-700 transition-all duration-200"
              title="消息"
              aria-label="消息"
            >
              <MessageCircle size={28} className="text-white" />
              <span className="mt-2 text-xs text-white">消息</span>
            </button>

            {/* 静音按钮 */}
            <button
              onClick={handleToggleMute}
              className={`flex flex-col items-center justify-center w-20 h-20 rounded-full transition-all duration-200 ${
                isMutedButton
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-gray-800 hover:bg-gray-700"
              }`}
              title={isMutedButton ? "取消静音" : "静音"}
              aria-label={isMutedButton ? "取消静音" : "静音"}
            >
              {isMutedButton ? (
                <MicOff size={28} className="text-white" />
              ) : (
                <Mic size={28} className="text-white" />
              )}
              <span className="mt-2 text-xs text-white">
                {isMutedButton ? "取消静音" : "静音"}
              </span>
            </button>

            {/* 扬声器按钮 */}
            {/* <button
              onClick={handleToggleSpeaker}
              className={`flex flex-col items-center justify-center w-20 h-20 rounded-full transition-all duration-200 ${
                isSpeakerEnabled 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-gray-800 hover:bg-gray-700'
              }`}
              title={isSpeakerEnabled ? '关闭扬声器' : '开启扬声器'}
              aria-label={isSpeakerEnabled ? '关闭扬声器' : '开启扬声器'}
            >
              {isSpeakerEnabled ? (
                <Volume2 size={28} className="text-white" />
              ) : (
                <VolumeX size={28} className="text-white" />
              )}
              <span className="mt-2 text-xs text-white">
                {isSpeakerEnabled ? '扬声器' : '听筒'}
              </span>
            </button> */}

            {/* 挂断按钮 */}
            <button
              onClick={handleEndCall}
              className="flex flex-col items-center justify-center w-20 h-20 rounded-full bg-red-600 hover:bg-red-700 transition-all duration-200"
              title="挂断"
              aria-label="挂断"
            >
              <PhoneOff size={28} className="text-white" />
              <span className="mt-2 text-xs text-white">挂断</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

ActiveCall.propTypes = {
  callId: PropTypes.string.isRequired,
  remoteUserId: PropTypes.number.isRequired,
  remoteUsername: PropTypes.string.isRequired,
  localAudioStream: PropTypes.object,
  remoteAudioStream: PropTypes.object,
  isMuted: PropTypes.bool,
  onToggleMute: PropTypes.func,
  onToggleSpeaker: PropTypes.func,
  onEndCall: PropTypes.func,
  onHold: PropTypes.func,
  className: PropTypes.string,
};

export default ActiveCall;
